import React, { useCallback, useEffect, useState } from "react"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Pagination from "@mui/material/Pagination"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import moment from "moment"
import { useSnackbar } from "notistack"
import PageContent from "components/common/LayoutComponents/PageContent"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { UseConfirmDialog, UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import {
  getMemberDetail,
  searchMembers,
  updateMemberBetaTester,
} from "services/member"
import { verifyResponse } from "utils/httpRequest"
import { StyledBetaTesters } from "./BetaTesters.style"

const PAGE_SIZE = 20

const getMemberId = (member) => member?.userId ?? member?.memberId ?? member?.id

const BetaTesters = () => {
  const [loading, setLoading] = useState(false)
  const [members, setMembers] = useState([])
  const [pageInfo, setPageInfo] = useState(null)
  const [pageNo, setPageNo] = useState(1)
  const [draftFilters, setDraftFilters] = useState({
    searchEmail: "",
    searchName: "",
  })
  const [appliedFilters, setAppliedFilters] = useState({
    searchEmail: "",
    searchName: "",
  })
  const [addForm, setAddForm] = useState({ email: "", memberId: "" })
  const [lookupMatch, setLookupMatch] = useState(null)

  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()
  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Remove Beta Tester",
    description: "Remove this member from the beta tester list?",
    agreeText: "Remove",
    disagreeText: "Cancel",
  })

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listMemberAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MEMBER.MEMBER_LIST,
      ]),
      manageBetaTesterAccess: AccessControl.VerifyAccess([
        PERMISSIONS.MEMBER.MEMBER_BETA_TESTER_UPDATE,
      ]),
    })
  }, [AccessControl.UserPermissions])

  const loadBetaTesters = useCallback(async () => {
    if (!AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_LIST])) return
    setLoading(true)
    try {
      const response = await searchMembers({
        pageNo,
        pageSize: PAGE_SIZE,
        searchEmail: appliedFilters.searchEmail || undefined,
        searchName: appliedFilters.searchName || undefined,
        isBetaTester: 1,
        isActive: 1,
      })
      verifyResponse(response)
      setMembers(response.data?.list || [])
      setPageInfo(response.data || null)
    } catch (error) {
      enqueueSnackbar(
        `Failed to load beta testers: ${error.message || error}`,
        { variant: "error" }
      )
    } finally {
      setLoading(false)
    }
  }, [pageNo, appliedFilters, AccessControl.UserPermissions, enqueueSnackbar])

  useEffect(() => {
    loadBetaTesters()
  }, [loadBetaTesters])

  const applyFilters = () => {
    setPageNo(1)
    setAppliedFilters({ ...draftFilters })
  }

  const lookupMember = async () => {
    const email = addForm.email.trim()
    const memberId = addForm.memberId.trim()
    if (!email && !memberId) {
      enqueueSnackbar("Enter an email or member ID to look up.", {
        variant: "warning",
      })
      return
    }

    setLoading(true)
    setLookupMatch(null)
    try {
      if (memberId) {
        const parsedId = parseInt(memberId, 10)
        if (!parsedId) {
          enqueueSnackbar("Member ID must be a valid number.", {
            variant: "warning",
          })
          return
        }
        const response = await getMemberDetail(parsedId)
        verifyResponse(response)
        const match = response.data
        if (!match) {
          enqueueSnackbar("No member found for that ID.", {
            variant: "warning",
          })
          return
        }
        if (
          email &&
          (match.email || "").toLowerCase() !== email.toLowerCase()
        ) {
          enqueueSnackbar(
            "That member ID does not match the email you entered.",
            { variant: "warning" }
          )
          return
        }
        setLookupMatch(match)
        return
      }

      const response = await searchMembers({
        pageNo: 1,
        pageSize: 10,
        searchEmail: email,
        isActive: 1,
      })
      verifyResponse(response)
      const list = response.data?.list || []
      const exact = list.find(
        (row) => (row.email || "").toLowerCase() === email.toLowerCase()
      )
      const match = exact || (list.length === 1 ? list[0] : null)
      if (!match) {
        enqueueSnackbar(
          list.length > 1
            ? "Multiple members matched. Enter the member ID to narrow it down."
            : "No active member found for that email.",
          { variant: "warning" }
        )
        return
      }
      setLookupMatch(match)
    } catch (error) {
      enqueueSnackbar(`Lookup failed: ${error.message || error}`, {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const addBetaTester = async (member) => {
    const memberId = getMemberId(member)
    if (!memberId) {
      enqueueSnackbar("Member ID is missing. Cannot add beta tester.", {
        variant: "error",
      })
      return
    }
    if (member.isBetaTester === 1 || member.isBetaTester === true) {
      enqueueSnackbar("This member is already a beta tester.", {
        variant: "info",
      })
      return
    }

    setLoading(true)
    try {
      const response = await updateMemberBetaTester({
        memberId,
        isBetaTester: true,
      })
      verifyResponse(response)
      enqueueSnackbar("Beta tester added successfully.", { variant: "success" })
      setAddForm({ email: "", memberId: "" })
      setLookupMatch(null)
      if (pageNo === 1) {
        await loadBetaTesters()
      } else {
        setPageNo(1)
      }
    } catch (error) {
      enqueueSnackbar(`Failed to add beta tester: ${error.message || error}`, {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const removeBetaTester = (member) => {
    const memberId = getMemberId(member)
    if (!memberId) {
      enqueueSnackbar("Member ID is missing. Cannot remove beta tester.", {
        variant: "error",
      })
      return
    }

    openConfirmDialog(async () => {
      setLoading(true)
      try {
        const response = await updateMemberBetaTester({
          memberId,
          isBetaTester: false,
        })
        verifyResponse(response)
        enqueueSnackbar("Beta tester removed successfully.", {
          variant: "success",
        })
        await loadBetaTesters()
      } catch (error) {
        enqueueSnackbar(
          `Failed to remove beta tester: ${error.message || error}`,
          { variant: "error" }
        )
      } finally {
        setLoading(false)
      }
    }, `Remove beta access for ${member.email || `member #${memberId}`}?`)
  }

  return (
    <PageContent>
      <StyledBetaTesters>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Beta Testers</h1>
            <p className="page-subtitle">
              View members marked as beta testers and add or remove early
              access for production beta features.
            </p>
          </div>
        </PageHeading>

        {!AccessControl.Access?.listMemberAccess && (
          <div className="page-empty">
            You do not have permission to view beta testers.
          </div>
        )}

        {AccessControl.Access?.listMemberAccess && (
          <div className="content-stack">
            {AccessControl.Access?.manageBetaTesterAccess && (
              <Card className="section-card">
                <CardContent>
                  <h3 className="section-title">Add Beta Tester</h3>
                  <p className="section-subtitle">
                    Look up an existing member by email or member ID, then add
                    them to the beta tester list.
                  </p>
                  <div className="form-grid">
                    <TextField
                      label="Member email"
                      value={addForm.email}
                      onChange={(event) =>
                        setAddForm({ ...addForm, email: event.target.value })
                      }
                      size="small"
                      autoComplete="off"
                    />
                    <TextField
                      label="Member ID (optional)"
                      value={addForm.memberId}
                      onChange={(event) =>
                        setAddForm({ ...addForm, memberId: event.target.value })
                      }
                      size="small"
                      autoComplete="off"
                    />
                    <Button variant="outlined" onClick={lookupMember}>
                      Look Up
                    </Button>
                  </div>

                  {lookupMatch && (
                    <div className="lookup-result">
                      <div className="lookup-meta">
                        <strong>
                          {lookupMatch.firstName || ""} {lookupMatch.lastName || ""}
                          {!lookupMatch.firstName && !lookupMatch.lastName
                            ? lookupMatch.email
                            : ""}
                        </strong>
                        <span>
                          ID {getMemberId(lookupMatch)} · {lookupMatch.email}
                          {lookupMatch.isBetaTester === 1 ||
                          lookupMatch.isBetaTester === true
                            ? " · Already a beta tester"
                            : ""}
                        </span>
                      </div>
                      <Button
                        variant="contained"
                        disabled={
                          lookupMatch.isBetaTester === 1 ||
                          lookupMatch.isBetaTester === true
                        }
                        onClick={() => addBetaTester(lookupMatch)}
                      >
                        Add as Beta Tester
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="section-card">
              <CardContent>
                <div className="filters">
                  <TextField
                    label="Filter by email"
                    value={draftFilters.searchEmail}
                    onChange={(event) =>
                      setDraftFilters({
                        ...draftFilters,
                        searchEmail: event.target.value,
                      })
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") applyFilters()
                    }}
                    size="small"
                    autoComplete="off"
                  />
                  <TextField
                    label="Filter by name"
                    value={draftFilters.searchName}
                    onChange={(event) =>
                      setDraftFilters({
                        ...draftFilters,
                        searchName: event.target.value,
                      })
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") applyFilters()
                    }}
                    size="small"
                    autoComplete="off"
                  />
                  <Button variant="contained" onClick={applyFilters}>
                    Search
                  </Button>
                  <Button variant="outlined" onClick={loadBetaTesters}>
                    Refresh
                  </Button>
                </div>

                {pageInfo?.total > 0 && (
                  <div className="search-result-info">
                    {pageInfo.total} beta tester
                    {pageInfo.total === 1 ? "" : "s"} found.
                  </div>
                )}

                <TableContainer component={Paper}>
                  <Table aria-label="beta testers table">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>First Name</TableCell>
                        <TableCell>Last Name</TableCell>
                        <TableCell>Join At</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="table-empty">
                            No beta testers found.
                          </TableCell>
                        </TableRow>
                      )}
                      {members.map((member) => {
                        const memberId = getMemberId(member)
                        return (
                          <TableRow key={`beta-tester-${memberId}`}>
                            <TableCell>{memberId}</TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{member.firstName}</TableCell>
                            <TableCell>{member.lastName}</TableCell>
                            <TableCell>
                              {member.registerAt
                                ? moment(member.registerAt).format("lll")
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {member.subscriptionPlan || "-"}
                            </TableCell>
                            <TableCell align="right">
                              {AccessControl.Access?.manageBetaTesterAccess && (
                                <div className="row-actions">
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    onClick={() => removeBetaTester(member)}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      {pageInfo && pageInfo.pages > 1 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <div className="pagination-section">
                              <Pagination
                                count={pageInfo.pages}
                                page={pageInfo.currentPageNo || pageNo}
                                onChange={(event, value) => setPageNo(value)}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {ConfirmDialog}
        <LoadingScreen loading={loading} />
      </StyledBetaTesters>
    </PageContent>
  )
}

export default BetaTesters
